<template>
  <div class="sales-page">
    <header class="page-heading">
      <div>
        <p>Inventaire</p>
        <h1>Ventes</h1>
        <span
          >Préparez vos annonces et suivez les objets jusqu’à leur vente.</span
        >
      </div>
      <button type="button" class="primary-action" @click="openCreatePopup">
        ＋ Nouvelle vente
      </button>
    </header>
    <section class="summary">
      <div>
        <strong>{{ totalSales }}</strong
        ><span>Ventes au total</span>
      </div>
      <div>
        <strong>{{ listedCount }}</strong
        ><span>En ligne chargées</span>
      </div>
      <div>
        <strong>{{ soldCount }}</strong
        ><span>Vendues chargées</span>
      </div>
      <div>
        <strong>{{ loadedRevenue }}</strong
        ><span>Revenus chargés</span>
      </div>
      <p>Les répartitions portent sur les ventes actuellement chargées.</p>
    </section>
    <section class="sales-panel">
      <nav class="tabs" aria-label="Statuts des ventes">
        <button
          v-for="tab in statusTabs"
          :key="tab.value"
          type="button"
          :class="{ active: filters.status === tab.value }"
          @click="setStatus(tab.value)"
        >
          {{ tab.label }}
        </button>
      </nav>
      <div class="toolbar">
        <label
          ><i class="fas fa-search" aria-hidden="true"></i
          ><input
            v-model="filters.contains"
            type="search"
            placeholder="Rechercher une vente…"
            @input="debouncedLoadSales" /></label
        ><select
          v-model="filters.source"
          aria-label="Filtrer par canal"
          @change="loadSales"
        >
          <option value="">Tous les canaux</option>
          <option v-for="(label, id) in SALE_CHANNELS" :key="id" :value="id">
            {{ label }}
          </option></select
        ><button
          v-if="hasActiveFilters"
          type="button"
          class="reset"
          @click="clearFilters"
        >
          Réinitialiser
        </button>
      </div>
      <div v-if="loading" class="state">Chargement des ventes…</div>
      <div v-else-if="!sales.length" class="state">
        <span>◎</span>
        <h2>Aucune vente trouvée</h2>
        <p>
          {{
            hasActiveFilters
              ? "Modifiez les filtres pour élargir les résultats."
              : "Créez une première vente à partir de vos objets."
          }}
        </p>
      </div>
      <div v-else class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Vente</th>
              <th>Canal</th>
              <th>Statut</th>
              <th>Prix</th>
              <th>Objets</th>
              <th>Date</th>
              <th><span class="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="sale in sales" :key="sale._key">
              <td>
                <button
                  type="button"
                  class="identity"
                  @click="showDetails(sale)"
                >
                  <span>{{ sale.title?.charAt(0).toUpperCase() || "V" }}</span>
                  <div>
                    <strong>{{ sale.title || "Vente sans titre" }}</strong
                    ><small
                      >#{{ sale._key
                      }}<template v-if="sale.description">
                        · {{ sale.description }}</template
                      ></small
                    >
                  </div>
                </button>
              </td>
              <td>
                <span class="source">{{ getSourceLabel(sale.source) }}</span>
              </td>
              <td>
                <span :class="['status', sale.status]"
                  ><i></i>{{ getStatusLabel(sale.status) }}</span
                >
              </td>
              <td>
                <strong>{{ formatMoney(sale.globalPrice) }}</strong>
              </td>
              <td>
                <div class="content">
                  <strong
                    >{{ sale.itemCount || sale.items?.length || 0 }} objet{{
                      (sale.itemCount || sale.items?.length || 0) > 1 ? "s" : ""
                    }}</strong
                  ><small v-if="sale.status === 'sold'">Vente finalisée</small>
                </div>
              </td>
              <td>
                <time>{{ formatDate(sale.saleDate || sale.updated_at) }}</time>
              </td>
              <td>
                <button
                  type="button"
                  class="details"
                  @click="showDetails(sale)"
                >
                  Détails →
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
    <SaleQuickPanel
      :visible="panelOpen"
      :sale="selectedSale || {}"
      @close="closePanel"
      @edit="openEditFromPanel"
      @delete="deleteSale"
      @open-item="
        (key) =>
          openDetail('item', key, { from: 'sale', fromId: selectedSale?._key })
      "
    /><SaleEditorPanel
      :visible="isEditPopupVisible"
      :sale="selectedSale"
      :initial-items="initialItems"
      @close="closeEditPopup"
      @saved="handleSaleSaved"
    />
  </div>
</template>
<script>
import { formatDate as formatDateText } from "../../clock";
import axios from "axios";
import { computed, onBeforeUnmount, onMounted, reactive, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import config from "@/config/config.js";
import SaleEditorPanel from "./SaleEditorPanel.vue";
import SaleQuickPanel from "./SaleQuickPanel.vue";
import { SALE_CHANNELS, saleChannelLabel } from "../../sources";
import { dropQueryParams } from "../../urlQuery";
import { openDetail } from "../../detailStack";
export default {
  name: "SalesList",
  components: { SaleEditorPanel, SaleQuickPanel },
  setup() {
    const route = useRoute(),
      router = useRouter();
    const sales = ref([]),
      totalSales = ref(0),
      loading = ref(false),
      panelOpen = ref(false),
      isEditPopupVisible = ref(false),
      selectedSale = ref(null),
      initialItems = ref([]),
      searchTimeout = ref(null),
      filters = reactive({ contains: "", status: "", source: "" }),
      statusTabs = [
        { label: "Toutes", value: "" },
        { label: "Brouillons", value: "draft" },
        { label: "En ligne", value: "listed" },
        { label: "Vendues", value: "sold" },
        { label: "Annulées", value: "cancelled" },
      ];
    const getSourceLabel = saleChannelLabel,
      getStatusLabel = (status) =>
        ({
          draft: "Brouillon",
          listed: "En ligne",
          sold: "Vendue",
          cancelled: "Annulée",
        })[status] || "Brouillon",
      formatMoney = (value) =>
        new Intl.NumberFormat("fr-FR", {
          style: "currency",
          currency: "EUR",
        }).format(Number(value) || 0),
      formatDate = (value) => formatDateText(value, undefined, "—");
    const hasActiveFilters = computed(() =>
        Boolean(filters.contains || filters.status || filters.source),
      ),
      listedCount = computed(
        () => sales.value.filter((sale) => sale.status === "listed").length,
      ),
      soldCount = computed(
        () => sales.value.filter((sale) => sale.status === "sold").length,
      ),
      loadedRevenue = computed(() =>
        formatMoney(
          sales.value
            .filter((sale) => sale.status === "sold")
            .reduce((sum, sale) => sum + (Number(sale.globalPrice) || 0), 0),
        ),
      );
    const loadSales = async () => {
      loading.value = true;
      try {
        const response = await axios.get(`${config.apiUrl}/sales`, {
          params: {
            contains: filters.contains || undefined,
            status: filters.status || undefined,
            source: filters.source || undefined,
            limit: 100,
          },
        });
        sales.value = Array.isArray(response.data?.result)
          ? response.data.result
          : [];
        totalSales.value = response.data?.total_items || sales.value.length;
      } catch (error) {
        console.error("Erreur lors du chargement des ventes", error);
        sales.value = [];
        totalSales.value = 0;
      } finally {
        loading.value = false;
      }
    };
    let detailsRequest = 0;
    const debouncedLoadSales = () => {
        clearTimeout(searchTimeout.value);
        searchTimeout.value = setTimeout(loadSales, 250);
      },
      setStatus = (value) => {
        if (filters.status === value) return;
        filters.status = value;
        loadSales();
      },
      clearFilters = () => {
        filters.contains = "";
        filters.status = "";
        filters.source = "";
        clearTimeout(searchTimeout.value);
        loadSales();
      },
      showDetails = async (sale) => {
        // Two sales opened quickly: the second never shows the first
        const request = ++detailsRequest;
        selectedSale.value = sale;
        panelOpen.value = true;
        try {
          const response = await axios.get(
            `${config.apiUrl}/sales/${sale._key}`,
          );
          if (request !== detailsRequest) return;
          selectedSale.value = response.data?.result || response.data || sale;
        } catch (error) {
          console.error("Détail de vente indisponible", error);
        }
      },
      closePanel = () => {
        panelOpen.value = false;
        selectedSale.value = null;
      },
      openCreatePopup = () => {
        selectedSale.value = null;
        initialItems.value = [];
        isEditPopupVisible.value = true;
      },
      openEditFromPanel = (sale) => {
        panelOpen.value = false;
        selectedSale.value = sale;
        initialItems.value = [];
        isEditPopupVisible.value = true;
      },
      closeEditPopup = () => {
        isEditPopupVisible.value = false;
        selectedSale.value = null;
        initialItems.value = [];
      },
      handleSaleSaved = async () => {
        isEditPopupVisible.value = false;
        selectedSale.value = null;
        await loadSales();
      },
      deleteSale = async (sale) => {
        if (
          !window.confirm(
            `Supprimer la vente « ${sale.title || "sans titre"} » ?`,
          )
        )
          return;
        try {
          await axios.delete(`${config.apiUrl}/sales/${sale._key}`);
          closePanel();
          await loadSales();
        } catch (error) {
          console.error("Suppression de la vente impossible", error);
        }
      };
    onMounted(async () => {
      await loadSales();
      if (route.query.create === "1") {
        try {
          initialItems.value = JSON.parse(
            sessionStorage.getItem("prexens:sale-draft-items") || "[]",
          );
        } catch {
          initialItems.value = [];
        }
        sessionStorage.removeItem("prexens:sale-draft-items");
        selectedSale.value = null;
        isEditPopupVisible.value = true;
        router.replace({ path: "/sales" });
      }
      // "?sale=<id>&edit=1": a sale to edit (link from a sheet)
      if (route.query.sale) {
        const key = String(route.query.sale);
        const edit = route.query.edit === "1";
        dropQueryParams("sale", "edit");
        try {
          const response = await axios.get(`${config.apiUrl}/sales/${key}`);
          const sale = response.data?.result || response.data;
          if (edit) openEditFromPanel(sale);
          else showDetails(sale);
        } catch (error) {
          console.error("Vente introuvable", error);
        }
      }
      window.addEventListener("prexens:changed", onChanged);
    });
    // A sale changed in a sheet opened elsewhere (deleted…): list reloaded
    const onChanged = (event) => {
      if (event.detail?.type === "sale") loadSales();
    };
    onBeforeUnmount(() => {
      clearTimeout(searchTimeout.value);
      window.removeEventListener("prexens:changed", onChanged);
    });
    return {
      SALE_CHANNELS,
      openDetail,
      clearFilters,
      closeEditPopup,
      closePanel,
      debouncedLoadSales,
      deleteSale,
      filters,
      formatDate,
      formatMoney,
      getSourceLabel,
      getStatusLabel,
      handleSaleSaved,
      hasActiveFilters,
      initialItems,
      isEditPopupVisible,
      listedCount,
      loadedRevenue,
      loading,
      openCreatePopup,
      openEditFromPanel,
      panelOpen,
      sales,
      selectedSale,
      showDetails,
      setStatus,
      soldCount,
      statusTabs,
      totalSales,
    };
  },
};
</script>
<style scoped>
.sales-page {
  --text: #202b3c;
  --muted: #6f7b8d;
  --line: #e3e8ef;
  --green: #168a64;
  min-height: 100%;
  padding: var(--page-gutter);
  background: #f5f7fa;
  color: var(--text);
  font-family: "DM Sans", sans-serif;
}
.page-heading,
.summary,
.sales-panel {
  max-width: var(--content-width);
  margin-inline: auto;
}
.page-heading {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
}
.page-heading p {
  margin: 0 0 8px;
  color: var(--green);
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}
.page-heading h1 {
  margin: 0;
  font-family: "Manrope", "DM Sans", sans-serif;
  font-size: var(--page-title-size);
  letter-spacing: -0.035em;
}
.page-heading span {
  display: block;
  margin-top: 8px;
  color: var(--muted);
  font-size: 14px;
}
.primary-action {
  height: 40px;
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  background: #172235;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
}
.summary {
  display: flex;
  margin-bottom: 16px;
  border: 1px solid var(--line);
  border-radius: 10px;
  background: #fff;
}
.summary div {
  display: flex;
  min-width: 150px;
  flex-direction: column;
  padding: 16px 20px;
  border-right: 1px solid var(--line);
}
.summary strong {
  font-size: 20px;
}
.summary span {
  margin-top: 4px;
  color: var(--muted);
  font-size: 12px;
}
.summary p {
  align-self: center;
  margin: 0 20px 0 auto;
  color: #87919f;
  font-size: 11px;
}
.sales-panel {
  overflow: hidden;
  border: 1px solid var(--line);
  border-radius: 12px;
  background: #fff;
}
.tabs {
  display: flex;
  height: 53px;
  gap: 24px;
  padding: 0 22px;
  border-bottom: 1px solid var(--line);
  overflow-x: auto;
}
.tabs button {
  position: relative;
  border: 0;
  background: none;
  color: #778294;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
}
.tabs button.active {
  color: var(--text);
}
.tabs button.active:after {
  position: absolute;
  right: 0;
  bottom: -1px;
  left: 0;
  height: 2px;
  background: var(--green);
  content: "";
}
.toolbar {
  display: flex;
  gap: 10px;
  padding: 14px 18px;
  border-bottom: 1px solid var(--line);
  background: #fbfcfd;
}
.toolbar label {
  display: flex;
  width: min(420px, 100%);
  height: 38px;
  align-items: center;
  gap: 9px;
  padding: 0 12px;
  border: 1px solid #d5dce5;
  border-radius: 8px;
  background: #fff;
  color: #8993a1;
}
.toolbar input {
  width: 100%;
  border: 0;
  outline: 0;
}
.toolbar select {
  height: 38px;
  padding: 0 34px 0 12px;
  border: 1px solid #d5dce5;
  border-radius: 8px;
  background: #fff;
  color: #4e5a6c;
}
.reset {
  border: 0;
  background: none;
  color: #687487;
  font-size: 12px;
  font-weight: 700;
}
.state {
  display: flex;
  min-height: 340px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: var(--muted);
}
.state > span {
  font-size: 26px;
  color: var(--green);
}
.state h2 {
  margin: 12px 0 5px;
  font-size: 17px;
}
.state p {
  margin: 0;
  font-size: 12px;
}
.table-wrap {
  overflow-x: auto;
}
.sales-panel table {
  width: 100%;
  table-layout: fixed;
  border-collapse: collapse;
}
.sales-panel th:first-child {
  width: auto;
}
.sales-panel th:nth-child(2) {
  width: 105px;
}
.sales-panel th:nth-child(3) {
  width: 115px;
}
.sales-panel th:nth-child(4) {
  width: 110px;
}
.sales-panel th:nth-child(5) {
  width: 120px;
}
.sales-panel th:nth-child(6) {
  width: 115px;
}
.sales-panel th:last-child {
  width: 95px;
}
.sales-panel th {
  padding: 11px 16px;
  border-bottom: 1px solid var(--line);
  background: #fff !important;
  color: #7d8796 !important;
  font-size: 11px;
  letter-spacing: 0.05em;
  text-align: left;
  text-transform: uppercase;
}
.sales-panel td {
  min-width: 0;
  padding: 13px 16px;
  border-bottom: 1px solid #edf0f4;
  vertical-align: middle;
}
.sales-panel tbody tr:nth-child(even) {
  background: #fff;
}
.sales-panel tbody tr:hover {
  background: #fafcfd;
}
.identity {
  display: flex;
  width: 100%;
  min-width: 0;
  align-items: center;
  gap: 11px;
  border: 0;
  background: none;
  text-align: left;
}
.identity > span {
  display: grid;
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  place-items: center;
  border-radius: 7px;
  background: #eaf7f2;
  color: var(--green);
  font-weight: 800;
}
.identity > div {
  min-width: 0;
}
.identity strong,
.identity small {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.identity strong {
  font-size: 13px;
}
.identity small {
  margin-top: 4px;
  color: #8993a1;
  font-size: var(--meta-size);
}
.source {
  padding: 5px 7px;
  border-radius: 6px;
  background: #eef2f6;
  color: #536073;
  font-size: var(--meta-size);
  font-weight: 700;
}
.status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 8px;
  border-radius: 6px;
  font-size: var(--meta-size);
  font-weight: 700;
}
.status i {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
}
.status.draft {
  background: #f0f2f4;
  color: #687384;
}
.status.listed {
  background: #edf4fb;
  color: #2e6098;
}
.status.sold {
  background: #eaf7f2;
  color: #177457;
}
.status.cancelled {
  background: #fff0ef;
  color: #b04a45;
}
.content {
  display: flex;
  flex-direction: column;
}
.content strong {
  font-size: 12px;
}
.content small,
time {
  margin-top: 3px;
  color: #84909f;
  font-size: var(--meta-size);
}
.details {
  height: 34px;
  padding: 0 10px;
  border: 1px solid #d8dee6;
  border-radius: 7px;
  background: #fff;
  color: #596679;
  font-size: 11px;
  font-weight: 700;
}
.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
}
@media (max-width: 1200px) {
  .sales-panel th:nth-child(2),
  .sales-panel td:nth-child(2) {
    display: none;
  }
  .summary p {
    display: none;
  }
  .summary div {
    min-width: 0;
    flex: 1;
  }
}
@media (max-width: 800px) {
  .sales-page {
    padding: 20px 14px;
  }
  .page-heading {
    align-items: flex-start;
    flex-direction: column;
  }
  .summary div:nth-child(3) {
    display: none;
  }
  .toolbar {
    flex-direction: column;
  }
  .toolbar label {
    width: 100%;
  }
  .sales-panel th:nth-child(5),
  .sales-panel td:nth-child(5),
  .sales-panel th:nth-child(6),
  .sales-panel td:nth-child(6) {
    display: none;
  }
}
@media (max-width: 560px) {
  .sales-panel th:nth-child(3),
  .sales-panel td:nth-child(3) {
    display: none;
  }
  .sales-panel th,
  .sales-panel td {
    padding: 11px 10px;
  }
  .details {
    display: none;
  }
}
</style>
